<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import KanbanAPI from 'dashboard/api/kanban';
import KanbanFoldersAPI from 'dashboard/api/kanbanFolders';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import LossReasonModal from './LossReasonModal.vue';
import KanbanDuplicateModal from './KanbanDuplicateModal.vue';
import WonValueModal from './WonValueModal.vue';
import OpportunityModalHeader from './components/OpportunityModalHeader.vue';
import OpportunityModalSidebar from './components/OpportunityModalSidebar.vue';
import OpportunityFieldsGrid from './components/OpportunityFieldsGrid.vue';
import OpportunityFlowsField from './components/OpportunityFlowsField.vue';
import OpportunityNotesSection from './components/OpportunityNotesSection.vue';
import OpportunityTasksSection from './components/OpportunityTasksSection.vue';
import OpportunityCalendarSection from './components/OpportunityCalendarSection.vue';
import OpportunityUtmSection from './components/OpportunityUtmSection.vue';
import OpportunityCustomFolderSection from './components/OpportunityCustomFolderSection.vue';
import OpportunityHistorySection from './components/OpportunityHistorySection.vue';

const props = defineProps({
  card: { type: Object, required: true },
  initialTab: { type: String, default: 'main' },
  isNew: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'deleted']);

const store = useStore();
const { t } = useI18n();

const title = ref('');
const status = ref('open');
const lossReason = ref('');
const cancelReason = ref('');
const waitingReason = ref('');
const value = ref('');
const forecast = ref('');
const forecastTime = ref('');
const assigneeId = ref(null);
const watcherIds = ref([]);
const source = ref('');
const products = ref([]);
const columnId = ref(null);
const pipelineId = ref(null);
const customAttributes = ref({});

const cardId = ref(props.isNew ? null : props.card.id);
let isCreating = false;
const pendingContact = ref(null);

// Lê sempre o card mais recente do store para refletir atualizações de contato.
// Em modo criação (antes do POST), injeta o contato selecionado localmente.
const liveCard = computed(() => {
  if (cardId.value === null) {
    return pendingContact.value
      ? { ...props.card, contact: pendingContact.value }
      : props.card;
  }
  return (
    store.state.kanban.cards.find(c => c.id === cardId.value) ?? props.card
  );
});

const pipelines = computed(() => store.getters['kanban/pipelines'] ?? []);
const selectedPipeline = computed(
  () => pipelines.value.find(p => p.id === pipelineId.value) ?? null
);
const isPostSalesPipeline = computed(
  () => selectedPipeline.value?.category === 'post_sales'
);

const initialized = ref(false);
let autoSaveTimer = null;

// Campos do formulário e como cada um vai para a API. O autosave manda só o que
// o usuário mudou desde a última sincronização (baseline): reenviar o formulário
// inteiro desfazia o que outra pessoa, uma automação ou o próprio backend
// (ex.: card ganho indo para a última etapa) tinha gravado enquanto o modal
// estava aberto.
const FORM_FIELDS = {
  title: { ref: title, key: 'title', toApi: v => v || null },
  value: {
    ref: value,
    key: 'value',
    toApi: v => (v !== '' && v != null ? parseFloat(v) : null),
  },
  forecast: { ref: forecast, key: 'closing_date', toApi: v => v || null },
  forecastTime: {
    ref: forecastTime,
    key: 'closing_time',
    toApi: v => v || null,
  },
  assigneeId: { ref: assigneeId, key: 'assignee_id', toApi: v => v || null },
  columnId: { ref: columnId, key: 'column_id', toApi: v => v || null },
  pipelineId: { ref: pipelineId, key: 'pipeline_id', toApi: v => v || null },
  status: { ref: status, key: 'status', toApi: v => v },
  lossReason: { ref: lossReason, key: 'loss_reason', toApi: v => v || null },
  cancelReason: {
    ref: cancelReason,
    key: 'cancel_reason',
    toApi: v => v || null,
  },
  waitingReason: {
    ref: waitingReason,
    key: 'waiting_reason',
    toApi: v => v || null,
  },
};

const clone = v => JSON.parse(JSON.stringify(v ?? null));
const sameValue = (a, b) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// custom_attributes do formulário, com origem e produtos que têm campo próprio
function formCustomAttributes() {
  return {
    ...customAttributes.value,
    opp_source: source.value || null,
    opp_products: products.value,
  };
}

let baseline = { fields: {}, custom: {} };

function takeBaseline() {
  const fields = {};
  Object.entries(FORM_FIELDS).forEach(([name, field]) => {
    fields[name] = clone(field.ref.value);
  });
  baseline = { fields, custom: clone(formCustomAttributes()) };
}

function isDirty(name) {
  return !sameValue(FORM_FIELDS[name].ref.value, baseline.fields[name]);
}

function changedPayload() {
  const payload = {};
  Object.entries(FORM_FIELDS).forEach(([name, field]) => {
    if (isDirty(name)) payload[field.key] = field.toApi(field.ref.value);
  });
  const custom = formCustomAttributes();
  const changedCustom = {};
  Object.keys({ ...custom, ...baseline.custom }).forEach(key => {
    if (!sameValue(custom[key], baseline.custom[key])) {
      changedCustom[key] = custom[key] ?? null;
    }
  });
  // o backend mescla custom_attributes: só as chaves alteradas vão no payload
  if (Object.keys(changedCustom).length)
    payload.custom_attributes = changedCustom;
  return payload;
}

async function autoSave() {
  if (!initialized.value) return;
  if (cardId.value === null) return;
  const payload = changedPayload();
  if (!Object.keys(payload).length) return;
  const previous = baseline;
  takeBaseline();
  const result = await store.dispatch('kanban/updateCard', {
    cardId: cardId.value,
    data: payload,
  });
  if (!result.success) {
    // volta a marcar os campos como pendentes para o próximo save tentar de novo
    baseline = previous;
    useAlert(result.errors?.[0] ?? t('KANBAN.OPPORTUNITY_MODAL.SAVE_ERROR'));
  }
}

function autoSaveDebounced() {
  clearTimeout(autoSaveTimer);
  autoSaveTimer = setTimeout(autoSave, 1500);
}

async function ensureCreated() {
  if (cardId.value !== null) return;
  if (isCreating) return;
  const contactId = liveCard.value?.contact?.id ?? null;
  if (!contactId) return;
  isCreating = true;
  try {
    const firstColumn =
      (props.card.columns ??
        store.getters['kanban/pipelines'].find(p => p.id === pipelineId.value)
          ?.columns ??
        [])[0] ?? null;

    const { data } = await KanbanAPI.createCard({
      pipeline_id: pipelineId.value,
      column_id: columnId.value ?? firstColumn?.id ?? null,
      title: title.value || null,
      contact_id: contactId,
    });

    const newCard = data?.data ?? data;
    cardId.value = newCard.id;
    columnId.value = newCard.column_id ?? columnId.value;

    await store.dispatch('kanban/fetchCards');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[ensureCreated] falha ao criar card:', err);
  } finally {
    isCreating = false;
  }
}

const isDeleting = ref(false);
const { isAdmin } = useAdmin();
const deleteConfirmDialogRef = ref(null);
const showDuplicateModal = ref(false);
const headerRef = ref(null);
const fieldsGridRef = ref(null);
const showLossReasonPicker = ref(false);
const showWaitingReasonPicker = ref(false);
const modalContainerRef = ref(null);
const contentPanelRef = ref(null);
const showWonValuePicker = ref(false);
const wonValueAnchor = ref(null);
const showFlowsPanel = ref(false);
const flowsFieldRef = ref(null);
const flowsButtonRef = ref(null);
const flowsPanelPos = ref({ top: 0, left: 0 });

function onTitleEnter() {
  fieldsGridRef.value?.focusContactSearch();
}

async function onContactSelected(contact) {
  pendingContact.value = contact;
  if (title.value === '') {
    title.value = contact.company_name ?? contact.name ?? '';
  }
  await ensureCreated();
}

const activeTab = ref(props.initialTab);
const opportunityAttributes = ref([]);
const folders = ref([]);

const accountId = computed(() => store.getters.getCurrentAccount?.id);
const agents = computed(() => store.getters['agents/getAgents'] ?? []);
const lossReasons = computed(() => selectedPipeline.value?.loss_reasons ?? []);
const cancelReasons = computed(
  () => selectedPipeline.value?.cancel_reasons ?? []
);
const waitingReasons = computed(
  () => selectedPipeline.value?.waiting_reasons ?? []
);

function openContact() {
  const contactId = liveCard.value.contact?.id;
  if (contactId && accountId.value) {
    window.open(
      `/app/accounts/${accountId.value}/contacts/${contactId}`,
      '_blank'
    );
  }
}

function openConversation() {
  const conversationId =
    liveCard.value.conversation?.id ?? liveCard.value.conversation_id;
  if (conversationId && accountId.value) {
    window.open(
      `/app/accounts/${accountId.value}/conversations/${conversationId}`,
      '_blank'
    );
  }
}

function toggleFlowsPanel() {
  if (!showFlowsPanel.value) {
    const rect = flowsButtonRef.value?.getBoundingClientRect();
    if (rect) {
      flowsPanelPos.value = {
        top: rect.bottom,
        left: rect.right,
      };
    }
  }
  showFlowsPanel.value = !showFlowsPanel.value;
}

function closeFlowsPanel() {
  showFlowsPanel.value = false;
}

function onFlowsPanelDocMousedown(e) {
  if (!showFlowsPanel.value) return;
  if (flowsButtonRef.value?.contains(e.target)) return;
  if (e.target.closest('[data-flows-panel]')) return;
  showFlowsPanel.value = false;
}

const pipelineColumns = computed(() => selectedPipeline.value?.columns ?? []);

const visibleFolders = computed(() => {
  const pipelineCategory = selectedPipeline.value?.category ?? 'sales';
  return folders.value.filter(
    f =>
      f.slug !== 'principal' && (!f.category || f.category === pipelineCategory)
  );
});

const activeFolder = computed(() => {
  if (!activeTab.value.startsWith('folder-')) return null;
  const folderId = Number(activeTab.value.replace('folder-', ''));
  return visibleFolders.value.find(f => f.id === folderId) ?? null;
});

const activeFolderAttrs = computed(() => {
  if (!activeFolder.value) return [];
  return opportunityAttributes.value.filter(
    a => a.folder_id === activeFolder.value.id
  );
});

function formatDateTime(ts) {
  if (!ts) return '—';
  const ms = ts > 1e10 ? ts : ts * 1000;
  const d = new Date(ms);
  const date = d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${date} - ${time}`;
}

const createdAt = computed(() => formatDateTime(props.card.created_at));

const updatedAt = computed(() => {
  const d =
    props.card.stage_changed_at ??
    props.card.status_changed_at ??
    props.card.updated_at ??
    null;
  return formatDateTime(d);
});

const SOURCE_TAGS = [
  'Meta Ads',
  'Google Ads',
  'Prospecção',
  'Indicação',
  'Instagram',
  'Linkedin',
  'Site',
  'Google',
  'Loja',
];

function initForm() {
  const c = props.card;

  title.value = c.title ?? c.contact?.name ?? '';
  status.value = c.status ?? 'open';
  lossReason.value = c.loss_reason ?? '';
  cancelReason.value = c.cancel_reason ?? '';
  waitingReason.value = c.waiting_reason ?? '';
  value.value = c.value != null ? parseFloat(c.value).toFixed(2) : '';
  forecast.value = c.closing_date ?? '';
  forecastTime.value = c.closing_time ?? '';
  assigneeId.value = c.assignee_id ?? null;
  watcherIds.value = c.custom_attributes?.watchers ?? [];
  columnId.value = c.column_id ?? null;
  pipelineId.value = c.pipeline_id ?? c.pipeline?.id ?? null;
  customAttributes.value = { ...(c.custom_attributes ?? {}) };

  const oppSource = c.custom_attributes?.opp_source ?? '';
  const contactSrc =
    (c.contact?.labels ?? []).find(l =>
      SOURCE_TAGS.includes(l.toLowerCase())
    ) ?? '';
  source.value = oppSource || contactSrc;
  products.value = c.custom_attributes?.opp_products ?? [];
  takeBaseline();

  initialized.value = false;
  nextTick(() => {
    initialized.value = true;
  });
}

// Quando o card muda no store (resposta do save, realtime, automação), traz os
// novos valores para o formulário, exceto nos campos que o usuário está editando.
function syncFromCard(c) {
  if (!c || c.id !== cardId.value) return;
  const incoming = {
    title: c.title ?? c.contact?.name ?? '',
    value: c.value != null ? parseFloat(c.value).toFixed(2) : '',
    forecast: c.closing_date ?? '',
    forecastTime: c.closing_time ?? '',
    assigneeId: c.assignee_id ?? null,
    columnId: c.column_id ?? null,
    pipelineId: c.pipeline_id ?? null,
    status: c.status ?? 'open',
    lossReason: c.loss_reason ?? '',
    cancelReason: c.cancel_reason ?? '',
    waitingReason: c.waiting_reason ?? '',
  };
  const wasInitialized = initialized.value;
  // atualizar os refs não pode disparar autosave nem refetch
  initialized.value = false;
  Object.entries(incoming).forEach(([name, val]) => {
    if (isDirty(name) || sameValue(FORM_FIELDS[name].ref.value, val)) return;
    FORM_FIELDS[name].ref.value = val;
    baseline.fields[name] = clone(val);
  });
  const custom = c.custom_attributes ?? {};
  const local = formCustomAttributes();
  const nextCustom = { ...customAttributes.value };
  Object.keys(custom).forEach(key => {
    if (!sameValue(local[key], baseline.custom[key])) return;
    if (key === 'opp_source' && custom[key]) source.value = custom[key];
    else if (key === 'opp_products') products.value = custom[key] ?? [];
    else nextCustom[key] = custom[key];
    baseline.custom[key] = clone(custom[key]);
  });
  customAttributes.value = nextCustom;
  watcherIds.value = custom.watchers ?? [];
  nextTick(() => {
    initialized.value = wasInitialized;
  });
}

watch(liveCard, syncFromCard);

async function loadOpportunityAttributes() {
  try {
    const { data } = await KanbanAPI.getOpportunityAttributes();
    opportunityAttributes.value = Array.isArray(data)
      ? data
      : (data.data ?? []);
  } catch {
    opportunityAttributes.value = [];
  }
}

async function loadFolders() {
  try {
    const { data } = await KanbanFoldersAPI.getFolders();
    folders.value = Array.isArray(data) ? data : (data.data ?? []);
  } catch {
    folders.value = [];
  }
}

async function addLossReason(reason) {
  const pipeline = selectedPipeline.value;
  if (!pipeline) return;
  const fieldKey = isPostSalesPipeline.value
    ? 'cancel_reasons'
    : 'loss_reasons';
  const existing = isPostSalesPipeline.value
    ? cancelReasons.value
    : lossReasons.value;
  if (existing.includes(reason)) return;
  const updated = [...new Set([...existing, reason])];
  await store.dispatch('kanban/updatePipeline', {
    id: pipeline.id,
    [fieldKey]: updated,
  });
}

function confirmLossReason(reason) {
  if (isPostSalesPipeline.value) {
    cancelReason.value = reason;
  } else {
    lossReason.value = reason;
  }
  showLossReasonPicker.value = false;
  autoSave();
}

async function addWaitingReason(reason) {
  const pipeline = selectedPipeline.value;
  if (!pipeline) return;
  if (waitingReasons.value.includes(reason)) return;
  const updated = [...new Set([...waitingReasons.value, reason])];
  await store.dispatch('kanban/updatePipeline', {
    id: pipeline.id,
    waiting_reasons: updated,
  });
}

function confirmWaitingReason(reason) {
  waitingReason.value = reason;
  showWaitingReasonPicker.value = false;
  autoSave();
}

function confirmWonValue(newValue) {
  if (newValue != null) value.value = parseFloat(newValue).toFixed(2);
  showWonValuePicker.value = false;
  autoSave();
}

function openDeleteConfirmDialog() {
  deleteConfirmDialogRef.value?.open();
}

async function deleteCard() {
  if (props.isNew && cardId.value === null) {
    emit('close');
    return;
  }
  isDeleting.value = true;
  try {
    const result = await store.dispatch(
      'kanban/deleteCard',
      cardId.value ?? props.card.id
    );
    if (result.success) {
      deleteConfirmDialogRef.value?.close();
      emit('deleted', props.card.id);
      emit('close');
    } else {
      useAlert(
        result.errors?.[0] ?? t('KANBAN.OPPORTUNITY_MODAL.DELETE_ERROR')
      );
    }
  } finally {
    isDeleting.value = false;
  }
}

function onCardReverted() {
  store.dispatch('kanban/fetchCards');
}

watch(
  () => props.card,
  (newCard, oldCard) => {
    if (newCard?.id != null && newCard.id === oldCard?.id) return;
    initForm();
  },
  { immediate: true }
);

// texto livre → debounce 1500ms
watch([title, value, forecast, forecastTime], autoSaveDebounced);

// criação lazy ao digitar em campos de texto
watch([title, value, forecast, forecastTime], async () => {
  if (props.isNew && cardId.value === null) {
    await ensureCreated();
  }
});

// seleções simples → imediato (com criação lazy se ainda não existe)
watch([assigneeId, source, products], async () => {
  if (props.isNew && cardId.value === null) {
    await ensureCreated();
  }
  autoSave();
});

// status → imediato, exceto lost/won que aguardam o picker de motivo/valor confirmar
watch(status, async newStatus => {
  if (newStatus === 'lost' || newStatus === 'won') return;
  if (props.isNew && cardId.value === null) {
    await ensureCreated();
  }
  autoSave();
});

// coluna ou pipeline → salva e faz fetchCards para mover o card no board
async function autoSaveAndRefetch() {
  await autoSave();
  store.dispatch('kanban/fetchCards');
}

async function onWatcherIdsUpdate(ids) {
  watcherIds.value = ids;
  if (cardId.value === null) return;
  customAttributes.value = { ...customAttributes.value, watchers: ids };
  baseline.custom.watchers = clone(ids);
  await store.dispatch('kanban/updateCard', {
    cardId: cardId.value,
    data: { custom_attributes: { watchers: ids } },
  });
}

watch(columnId, (newVal, oldVal) => {
  if (!initialized.value || newVal === oldVal || !isDirty('columnId')) return;
  autoSaveAndRefetch();
});

watch(pipelineId, async (newVal, oldVal) => {
  if (!initialized.value || newVal === oldVal || !isDirty('pipelineId')) return;
  // Ao trocar pipeline, seta coluna para a primeira coluna do novo pipeline
  // e faz um único save com os dois valores corretos
  const firstCol = (pipelines.value.find(p => p.id === newVal)?.columns ??
    [])[0];
  // Desabilita initialized para o watcher de columnId não disparar save duplicado
  initialized.value = false;
  columnId.value = firstCol?.id ?? null;
  initialized.value = true;
  await autoSave();
  store.dispatch('kanban/fetchCards');
});

// custom attributes de pastas/marketing/UTM extra → debounce
watch(customAttributes, autoSaveDebounced, { deep: true });

onMounted(() => {
  loadOpportunityAttributes();
  loadFolders();
  store.dispatch('agents/get');

  if (props.isNew) {
    nextTick(() => {
      headerRef.value?.focusTitle();
    });
  }

  document.addEventListener('mousedown', onFlowsPanelDocMousedown);
});

onBeforeUnmount(() => {
  clearTimeout(autoSaveTimer);
  document.removeEventListener('mousedown', onFlowsPanelDocMousedown);
});
</script>

<template>
  <div
    class="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40"
    @click.self="emit('close')"
  >
    <div
      ref="modalContainerRef"
      class="relative bg-n-background rounded-xl shadow-2xl w-full h-full md:max-w-[680px] md:mx-4 md:h-[600px] flex flex-col overflow-hidden"
    >
      <!-- Header -->
      <OpportunityModalHeader
        ref="headerRef"
        :card="liveCard"
        :title="title"
        :status="status"
        :loss-reason="isPostSalesPipeline ? cancelReason : lossReason"
        :picking-loss-reason="showLossReasonPicker"
        :waiting-reason="waitingReason"
        :picking-waiting-reason="showWaitingReasonPicker"
        :pipeline-category="selectedPipeline?.category ?? 'sales'"
        @close="emit('close')"
        @open-contact="openContact"
        @update:title="title = $event"
        @update:status="status = $event"
        @pick-loss-reason="
          () => {
            showLossReasonPicker = true;
          }
        "
        @pick-waiting-reason="
          () => {
            showWaitingReasonPicker = true;
          }
        "
        @pick-won-value="
          rect => {
            wonValueAnchor = rect;
            showWonValuePicker = true;
          }
        "
        @title-enter="onTitleEnter"
      />

      <!-- Corpo: sidebar + conteúdo -->
      <div class="flex flex-col md:flex-row flex-1 min-h-0 overflow-hidden">
        <OpportunityModalSidebar
          :active-tab="activeTab"
          :folders="visibleFolders"
          :card="liveCard"
          @tab-change="activeTab = $event"
        />

        <div ref="contentPanelRef" class="flex-1 overflow-y-auto px-5 py-4">
          <OpportunityFieldsGrid
            v-if="activeTab === 'main'"
            ref="fieldsGridRef"
            :card="liveCard"
            :is-new="isNew"
            :value="value"
            :forecast="forecast"
            :forecast-time="forecastTime"
            :assignee-id="assigneeId"
            :source="source"
            :column-id="columnId"
            :pipeline-id="pipelineId"
            :pipelines="pipelines"
            :columns="pipelineColumns"
            :agents="agents"
            :watcher-ids="watcherIds"
            :products="products"
            @update:value="value = $event"
            @update:forecast="forecast = $event"
            @update:forecast-time="forecastTime = $event"
            @update:assignee-id="assigneeId = $event"
            @update:source="source = $event"
            @update:column-id="columnId = $event"
            @update:pipeline-id="pipelineId = $event"
            @update:watcher-ids="onWatcherIdsUpdate"
            @update:products="products = $event"
            @contact-updated="store.dispatch('kanban/fetchCards')"
            @contact-selected="onContactSelected"
            @open-contact="openContact"
          />
          <OpportunityNotesSection
            v-else-if="activeTab === 'notes'"
            :card="liveCard"
          />
          <OpportunityTasksSection
            v-else-if="activeTab === 'tasks'"
            :card="liveCard"
          />
          <OpportunityCalendarSection
            v-else-if="activeTab === 'calendar'"
            :card="liveCard"
          />
          <OpportunityUtmSection
            v-else-if="activeTab === 'utm'"
            :card="liveCard"
            :attributes="opportunityAttributes"
            @update="customAttributes = $event"
          />
          <OpportunityCustomFolderSection
            v-else-if="activeTab.startsWith('folder-')"
            :attributes="activeFolderAttrs"
            :custom-attributes="customAttributes"
            :folder-name="activeFolder?.name ?? ''"
            :card-id="liveCard.id"
            @update="customAttributes = $event"
          />
          <OpportunityHistorySection
            v-else-if="activeTab === 'history'"
            :card="liveCard"
            @card-updated="onCardReverted"
          />
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-end justify-between px-5 py-3 border-t border-n-weak flex-shrink-0"
      >
        <div class="flex flex-col gap-0.5">
          <span class="text-[9px] md:text-[11px] text-n-slate-9">
            {{ t('KANBAN.OPPORTUNITY_MODAL.CREATED_AT') }} {{ createdAt }}
          </span>
          <span class="text-[9px] md:text-[11px] text-n-slate-9">
            {{ t('KANBAN.OPPORTUNITY_MODAL.UPDATED_AT') }} {{ updatedAt }}
          </span>
        </div>

        <div class="flex items-center gap-0 md:gap-1">
          <button
            v-if="liveCard.contact?.id"
            ref="flowsButtonRef"
            class="relative w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
            :title="t('KANBAN.FLOWS_FIELD.LABEL')"
            @click.stop="toggleFlowsPanel"
          >
            <span class="i-lucide-bot size-5 md:size-6" />
            <span
              v-if="flowsFieldRef?.hasActiveFlows"
              class="absolute top-1 right-1 w-2 h-2 rounded-full bg-n-teal-9 border border-white dark:border-n-background"
            />
          </button>
          <button
            v-if="liveCard.contact?.id"
            class="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
            :title="t('KANBAN.OPPORTUNITY_MODAL.GO_TO_CONTACT')"
            @click="openContact"
          >
            <span class="i-lucide-user size-5 md:size-6" />
          </button>
          <button
            v-if="liveCard.conversation?.id || liveCard.conversation_id"
            class="relative w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
            :title="t('KANBAN.OPPORTUNITY_MODAL.GO_TO_CONVERSATION')"
            @click="openConversation"
          >
            <span class="i-lucide-message-square size-5 md:size-6" />
            <span
              v-if="liveCard.conversation_alert"
              class="absolute top-1 right-1 w-2 h-2 rounded-full border border-white dark:border-n-background"
              :class="
                liveCard.conversation_alert === 'today'
                  ? 'bg-n-teal-9'
                  : 'bg-n-ruby-9'
              "
            />
          </button>
          <button
            v-if="cardId !== null && !isNew"
            class="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
            :title="t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE')"
            @click="showDuplicateModal = true"
          >
            <span class="i-lucide-copy size-4 md:size-5" />
          </button>
          <button
            v-if="isAdmin || (isNew && cardId === null)"
            class="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded text-n-slate-9 hover:text-n-ruby-9 hover:bg-n-ruby-2 transition-colors disabled:opacity-50"
            :disabled="isDeleting"
            :title="t('KANBAN.OPPORTUNITY_MODAL.DELETE')"
            @click="openDeleteConfirmDialog"
          >
            <span class="i-lucide-trash-2 size-5 md:size-6" />
          </button>
        </div>
      </div>
    </div>

    <LossReasonModal
      v-if="showLossReasonPicker"
      :reasons="isPostSalesPipeline ? cancelReasons : lossReasons"
      :anchor="null"
      :container-rect="contentPanelRef?.getBoundingClientRect()"
      in-modal
      @confirm="confirmLossReason"
      @cancel="showLossReasonPicker = false"
      @create="addLossReason"
    />

    <LossReasonModal
      v-if="showWaitingReasonPicker"
      :reasons="waitingReasons"
      :anchor="null"
      :container-rect="contentPanelRef?.getBoundingClientRect()"
      color="amber"
      in-modal
      @confirm="confirmWaitingReason"
      @cancel="showWaitingReasonPicker = false"
      @create="addWaitingReason"
    />

    <WonValueModal
      v-if="showWonValuePicker"
      :initial-value="value"
      :anchor="wonValueAnchor"
      @confirm="confirmWonValue"
      @cancel="showWonValuePicker = false"
    />

    <KanbanDuplicateModal
      v-if="showDuplicateModal"
      :card="liveCard"
      @close="showDuplicateModal = false"
      @duplicated="showDuplicateModal = false"
    />

    <Teleport to="body">
      <div
        v-if="liveCard.contact?.id"
        data-flows-panel
        class="fixed z-[10002]"
        :style="{
          top: flowsPanelPos.top + 'px',
          left: flowsPanelPos.left + 'px',
        }"
      >
        <OpportunityFlowsField
          ref="flowsFieldRef"
          :contact-id="liveCard.contact?.id ?? null"
          :open="showFlowsPanel"
          @close="closeFlowsPanel"
        />
      </div>
    </Teleport>

    <Dialog
      ref="deleteConfirmDialogRef"
      type="alert"
      width="sm"
      :title="t('KANBAN.OPPORTUNITY_MODAL.DELETE_CONFIRM_TITLE')"
      :description="t('KANBAN.OPPORTUNITY_MODAL.DELETE_CONFIRM_DESC')"
      @confirm="deleteCard"
    >
      <template #footer>
        <div class="flex items-center justify-center w-full gap-2">
          <Button
            faded
            slate
            sm
            :label="t('KANBAN.OPPORTUNITY_MODAL.DELETE_CONFIRM_CANCEL')"
            type="button"
            @click="deleteConfirmDialogRef?.close()"
          />
          <Button
            ruby
            sm
            :label="t('KANBAN.OPPORTUNITY_MODAL.DELETE_CONFIRM_CONFIRM')"
            :is-loading="isDeleting"
            :disabled="isDeleting"
            type="button"
            @click="deleteCard"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
